import type { Dispatch, SetStateAction } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Icon } from '../index';
import type { useTheme } from './theme';
import { makeStyles } from './loggerStyles';

// Weight-stepper bar (inbox 024b2c93). Rendered only when the weight
// cell is focused so the row stays compact otherwise. Plates icon + unit
// toggle + ± sign + magnitude buttons. Per
// feedback_user_visual_accessibility all tap targets stay ≥44pt and the
// input itself is never shrunk to make room for them.
export function SetRowStepperBar({
  unit,
  onOpenPlates,
  onToggleUnit,
  stepSign,
  setStepSign,
  stepMagnitudes,
  applyDelta,
  theme,
}: {
  unit: 'kg' | 'lb';
  onOpenPlates?: () => void;
  onToggleUnit: () => void;
  stepSign: 1 | -1;
  setStepSign: Dispatch<SetStateAction<1 | -1>>;
  stepMagnitudes: number[];
  applyDelta: (delta: number) => void;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  return (
    <View style={styles.stepperRow}>
      {onOpenPlates ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('mobile.logger.plates')}
          onPress={onOpenPlates}
          hitSlop={8}
          style={({ pressed }) => [
            styles.stepperButton,
            styles.stepperButtonPlates,
            pressed && styles.stepperButtonPressed,
          ]}
        >
          <Icon
            name="weight"
            size={theme.typography.sizes.sm}
            color={theme.palette.ink2}
          />
        </Pressable>
      ) : null}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('mobile.logger.unitA11y', { unit })}
        onPress={onToggleUnit}
        hitSlop={8}
        style={({ pressed }) => [
          styles.stepperButton,
          styles.stepperButtonUnit,
          pressed && styles.stepperButtonPressed,
        ]}
      >
        <Text style={[styles.stepperButtonText, styles.stepperButtonTextUnit]}>
          {unit.toUpperCase()}
        </Text>
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ selected: stepSign === -1 }}
        accessibilityLabel={stepSign === 1 ? 'step add' : 'step subtract'}
        onPress={() => setStepSign((s) => (s === 1 ? -1 : 1))}
        hitSlop={8}
        style={({ pressed }) => [
          styles.stepperButton,
          styles.stepperButtonSign,
          stepSign === 1
            ? styles.stepperButtonSignPositive
            : styles.stepperButtonSignNegative,
          pressed && styles.stepperButtonPressed,
        ]}
      >
        <Text
          style={[
            styles.stepperButtonText,
            stepSign === 1
              ? styles.stepperButtonTextSignPositive
              : styles.stepperButtonTextSignNegative,
          ]}
        >
          {stepSign === 1 ? '+' : '−'}
        </Text>
      </Pressable>
      {stepMagnitudes.map((magnitude) => {
        const label = magnitude.toString();
        return (
          <Pressable
            key={magnitude}
            accessibilityRole="button"
            accessibilityLabel={`${stepSign === 1 ? '+' : '−'}${label}`}
            onPress={() => applyDelta(magnitude * stepSign)}
            hitSlop={8}
            style={({ pressed }) => [
              styles.stepperButton,
              pressed && styles.stepperButtonPressed,
            ]}
          >
            <Text style={styles.stepperButtonText}>{label}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}
